import { useEffect, useRef, useState } from "react";

export const DEFAULT_CARD_WIDTH = 420;
export function clampPositionToViewport({
    position,
    cardWidth = DEFAULT_CARD_WIDTH,
    cardHeight,
    minLeft = 8,
    minTop = 56,
    minBottom = 8
}: {
    position: { left: number; top: number };
    cardWidth?: number;
    cardHeight?: number;
    minLeft?: number;
    minTop?: number;
    minBottom?: number;
}): { left: number; top: number } {
    const maxLeft = Math.max(0, window.innerWidth - cardWidth - minLeft);

    let clampedTop = Math.max(position.top, minTop);

    // If cardHeight is provided, also constrain the bottom
    if (cardHeight !== undefined && cardHeight > 0) {
        const maxTop = Math.max(0, window.innerHeight - cardHeight - minBottom);
        clampedTop = Math.min(clampedTop, maxTop);
    }

    return { left: Math.min(Math.max(position.left, minLeft), maxLeft), top: clampedTop };
}

export const DRAGGABLE_CARD_ANIMATION = {
    initial: { opacity: 0, scale: 0.95, y: -20 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: -20 },
    transition: {
        duration: 0.1,
        ease: "easeOut" as const,
        type: "spring" as const,
        stiffness: 300,
        damping: 30
    }
};

export const DRAGGABLE_CARD_CLASSES =
    "group fixed z-50 flex cursor-move flex-col gap-3 rounded-xl border bg-white shadow-2xl";

interface UseDraggableCardOptions {
    /** Whether the card is open/visible */
    isOpen: boolean;
    /** Width of the card in pixels */
    cardWidth?: number;
    /** Height of the card in pixels (optional, for fixed height cards) */
    cardHeight?: number;
    /** Minimum left position */
    minLeft?: number;
    /** Minimum top position - accommodates top nav bar*/
    minTop?: number;
    /** Minimum bottom padding from bottom edge */
    minBottom?: number;
    /** Anchor element to position the card relative to (optional) */
    anchorElement?: HTMLElement | null;
    /** Initial position when no anchor element is provided */
    initialPosition?: { left: number; top: number };
}

interface UseDraggableCardReturn {
    /** Current position of the card */
    position: { left: number; top: number };
    /** Function to update position externally */
    setPosition: React.Dispatch<React.SetStateAction<{ left: number; top: number }>>;
    /** Ref to attach to the card element */
    cardRef: React.RefObject<HTMLDivElement | null>;
    /** Function to call on mouseDown to start dragging */
    startDrag: (e: React.MouseEvent) => void;
}

export function useDraggableCard({
    isOpen,
    cardWidth = DEFAULT_CARD_WIDTH,
    cardHeight,
    minLeft = 8,
    minTop = 56,
    minBottom = 8,
    anchorElement,
    initialPosition = { left: minLeft, top: minTop }
}: UseDraggableCardOptions): UseDraggableCardReturn {
    const [position, setPosition] = useState(initialPosition);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const isDraggingRef = useRef(false);
    const dragOffsetRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

    // Calculate initial position based on anchor element
    useEffect(() => {
        if (isOpen && anchorElement) {
            const rect = anchorElement.getBoundingClientRect();
            const initialLeft = rect.right - cardWidth;
            const initialTop = rect.bottom + 8;
            const actualHeight = cardHeight || cardRef.current?.offsetHeight || 0;

            setPosition(
                clampPositionToViewport({
                    position: { left: initialLeft, top: initialTop },
                    cardWidth,
                    cardHeight: actualHeight,
                    minLeft,
                    minTop,
                    minBottom
                })
            );
        }
    }, [isOpen, anchorElement, cardWidth, cardHeight, minLeft, minTop, minBottom]);

    // Handle window resize to keep card in viewport
    useEffect(() => {
        if (!isOpen) return;

        function onResize() {
            setPosition((pos) => {
                const actualHeight = cardHeight || cardRef.current?.offsetHeight || 0;

                return clampPositionToViewport({
                    position: pos,
                    cardWidth,
                    cardHeight: actualHeight,
                    minLeft,
                    minTop,
                    minBottom
                });
            });
        }
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [isOpen, cardWidth, cardHeight, minLeft, minTop, minBottom]);

    // Handle dragging
    useEffect(() => {
        if (!isOpen) return;

        function handleMouseMove(e: MouseEvent) {
            if (!isDraggingRef.current) return;
            const newLeft = e.clientX - dragOffsetRef.current.dx;
            const newTop = e.clientY - dragOffsetRef.current.dy;

            // Get actual card height if available, otherwise use provided cardHeight
            const actualHeight = cardHeight || cardRef.current?.offsetHeight || 0;

            setPosition(
                clampPositionToViewport({
                    position: { left: newLeft, top: newTop },
                    cardWidth,
                    cardHeight: actualHeight,
                    minLeft,
                    minTop,
                    minBottom
                })
            );
        }

        function handleMouseUp() {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.userSelect = "";
                document.body.style.cursor = "";
            }
        }

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            // Clean up body styles if card closes or unmounts while dragging
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.userSelect = "";
                document.body.style.cursor = "";
            }
        };
    }, [isOpen, cardWidth, cardHeight, minLeft, minTop, minBottom]);

    function startDrag(e: React.MouseEvent) {
        if (e.button !== 0) return;
        isDraggingRef.current = true;
        dragOffsetRef.current = { dx: e.clientX - position.left, dy: e.clientY - position.top };
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
    }

    return { position, setPosition, cardRef, startDrag };
}
